import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type UserTier = 'subscribed' | 'invited' | 'trial' | 'internal' | 'super_admin' | 'free-gift';

interface UserAccessData {
  tier: UserTier | null;
  hasAccess: boolean;
  isOnTrial: boolean;
  isTrialExpired: boolean;
  daysRemaining: number;
  trialEnd: string | null;
  isFreeGift: boolean;
}

export const useUserAccess = () => {
  const [accessData, setAccessData] = useState<UserAccessData>({
    tier: null,
    hasAccess: false,
    isOnTrial: false,
    isTrialExpired: false,
    daysRemaining: 0,
    trialEnd: null,
    isFreeGift: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const checkUserAccess = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Get user's tier from profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('tier')
        .eq('id', user.id)
        .single();

      // If profile doesn't exist but user has brands, treat as legacy user with access
      if (profileError && profileError.code === 'PGRST116') {
        // Check if user has brands (legacy users)
        const { data: brands } = await supabase
          .from('brands')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
          
        if (brands && brands.length > 0) {
          // Legacy user with brands - grant access
          setAccessData({
            tier: null,
            hasAccess: true,
            isOnTrial: false,
            isTrialExpired: false,
            daysRemaining: 0,
            trialEnd: null,
            isFreeGift: false,
          });
          return;
        }
      }

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return;
      }

      const tier = profile?.tier as UserTier;
      let hasAccess = ['subscribed', 'invited', 'internal', 'super_admin'].includes(tier);
      let isOnTrial = false;
      let isTrialExpired = false;
      let daysRemaining = 0;
      let trialEnd = null;

      // If user is super_admin, internal, invited, or subscribed, they have access regardless of trial status
      if (['super_admin', 'internal', 'invited', 'subscribed'].includes(tier)) {
        setAccessData({
          tier,
          hasAccess: true,
          isOnTrial: false,
          isTrialExpired: false,
          daysRemaining: 0,
          trialEnd: null,
          isFreeGift: false,
        });
        return;
      }

      // If user is on trial, check trial details
      if (tier === 'trial') {
        const { data: trialData, error: trialError } = await supabase
          .from('trials')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (!trialError && trialData?.trial_end) {
          const trialEndDate = new Date(trialData.trial_end);
          const now = new Date();
          const timeDiff = trialEndDate.getTime() - now.getTime();
          daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
          
          isOnTrial = daysRemaining > 0;
          isTrialExpired = daysRemaining <= 0;
          trialEnd = trialData.trial_end;
          
          // If trial is expired, user has no access
          if (isTrialExpired) {
            hasAccess = false;
          } else {
            hasAccess = true;
          }
        } else {
          // If no trial data found but tier is trial, try to create one
          console.warn('No trial record found for trial-tier user, attempting to create one');
          
          try {
            const trialEndDate = new Date();
            trialEndDate.setDate(trialEndDate.getDate() + 7);
            
            const { error: createError } = await supabase
              .from('trials')
              .insert({
                user_id: user.id,
                subscribed: true,
                trial_tier: 'trial',
                trial_end: trialEndDate.toISOString(),
              });
            
            if (!createError) {
              // Retry checking access after creating trial
              await checkUserAccess();
              return;
            }
          } catch (err) {
            console.error('Failed to create trial record:', err);
          }
          
          // If still no trial data, consider it expired
          isTrialExpired = true;
          hasAccess = false;
        }
      }

      // If user is on free-gift, check trial details (same as trial but different tier)
      if (tier === 'free-gift') {
        const { data: trialData, error: trialError } = await supabase
          .from('trials')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (!trialError && trialData?.trial_end) {
          const trialEndDate = new Date(trialData.trial_end);
          const now = new Date();
          const timeDiff = trialEndDate.getTime() - now.getTime();
          daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
          
          isOnTrial = daysRemaining > 0;
          isTrialExpired = daysRemaining <= 0;
          trialEnd = trialData.trial_end;
          
          if (isTrialExpired) {
            hasAccess = false;
          } else {
            hasAccess = true;
          }
        } else {
          // If no trial data found but tier is free-gift, try to create one
          console.warn('No trial record found for free-gift user, attempting to create one');
          
          try {
            const trialEndDate = new Date();
            trialEndDate.setDate(trialEndDate.getDate() + 7);
            
            const { error: createError } = await supabase
              .from('trials')
              .insert({
                user_id: user.id,
                subscribed: true,
                trial_tier: 'free-gift',
                trial_end: trialEndDate.toISOString(),
              });
            
            if (!createError) {
              // Retry checking access after creating trial
              await checkUserAccess();
              return;
            }
          } catch (err) {
            console.error('Failed to create trial record:', err);
          }
          
          // If still no trial data, consider it expired
          isTrialExpired = true;
          hasAccess = false;
        }
      }

      setAccessData({
        tier,
        hasAccess,
        isOnTrial,
        isTrialExpired,
        daysRemaining: Math.max(0, daysRemaining),
        trialEnd,
        isFreeGift: tier === 'free-gift',
      });
    } catch (error) {
      console.error('Error checking user access:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserTier = async (newTier: UserTier) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ tier: newTier })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating user tier:', error);
        return false;
      }

      // Refresh access data
      await checkUserAccess();
      return true;
    } catch (error) {
      console.error('Error updating user tier:', error);
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      checkUserAccess();
    }
  }, [user]);

  return {
    ...accessData,
    isLoading,
    checkUserAccess,
    updateUserTier,
  };
};